<template>
    <div class="shops-navigator shops-wrap flex flex-col-reverse md:flex-row w-full md:absolute md:pin">
        <div class="shops-controls flex flex-col flex-shrink relative bg-white border-grey-light border-r md:max-w-sm">
            <div class="shops-controls__header w-full z-20">
                <TheShopsSearch class="bg-grey-light p-3" />
                <div class="shops-controls__controls border-grey-light border-b flex items-center flex-wrap">
                    <transition name="slide-left-20">
                        <div class="flex items-center justify-between flex-grow relative" v-if="shopDetailsOpen || shopFiltersOpen">
                            <button type="button" class="btn text-sm font-bold" @click="toggleShopList()">
                                <IconChevronLeft viewBox="0 0 24 24" class="inline-block w-6 h-6 mr"/>
                                Back to Results
                            </button>
                            <button type="button"
                            class="btn text-red text-sm justify-end"
                                v-if="shopFiltersOpen && countFiltersEnabled > 0"
                                @click="clearShopsFilters()">
                                <IconTrash2 viewBox="0 0 24 24" class="inline-block w-3 h-3 mr-1"/>
                                Clear Filters ({{ countFiltersEnabled }})
                            </button>
                        </div>
                    </transition>
                    <transition name="slide-right-20">
                        <div class="flex items-center flex-grow relative" v-if="!shopDetailsOpen && !shopFiltersOpen">
                            <strong class="flex-grow ml-6" v-if="!currentlySearching">{{ shopsCount }} shops found</strong>
                            <strong class="flex-grow ml-6" v-else>Searching for shops...</strong>
                            <button type="button"
                                class="btn text-red text-sm justify-end"
                                v-if="countFiltersEnabled > 0"
                                @click="clearShopsFilters()">
                                <IconTrash2 viewBox="0 0 24 24" class="inline-block w-3 h-3 mr-1"/>
                                Clear Filters ({{ countFiltersEnabled }})
                            </button>
                            <button type="button" class="btn text-sm font-bold justify-end" v-on:click="toggleShopFilters()">
                                <IconSliders viewBox="0 0 24 24"
                                    class="inline-block w-4 h-4 mr-2"
                                    alt="Filters" />
                                Filter Results
                            </button>
                        </div>
                    </transition>
                </div>
            </div>
            <div class="overflow-x-hidden w-full flex-grow relative">
                <transition name="slide-left-full">
                    <div class="absolute pin-y w-full z-10 scrolling-touch overflow-y-auto"
                        v-if="shopFiltersOpen">
                        <div class="p-6 pb-0" v-if="shopsFilters.technicians.length">
                            <h3 class="text-base mb-2">Technicians</h3>
                            <label
                                v-for="(item, index) in shopsFilters.technicians" :for="'tech' + item | stripSpaces"
                                :key="index">
                                <input type="checkbox"
                                    :id="'tech' + item | stripSpaces"
                                    :value="item"
                                    class="hidden"
                                    ref="filterInputs"
                                    v-model="shopsFiltersEnabled.technicians">
                                <span class="btn-sm btn-white-bordered btn-filter-toggle border text-xs">{{ item }}</span>
                            </label>
                        </div>
                        <div class="p-6 pb-0" v-if="shopsFilters.repairServices.length">
                            <h3 class="text-base mb-2">Repair Services</h3>
                            <label
                                v-for="(item, index) in shopsFilters.repairServices" :for="'repService' + item | stripSpaces"
                                :key="index">
                                <input type="checkbox"
                                    :id="'repService' + item | stripSpaces"
                                    :value="item"
                                    class="hidden"
                                    ref="filterInputs"
                                    v-model="shopsFiltersEnabled.repairServices">
                                <span class="btn-sm btn-white-bordered btn-filter-toggle border text-xs">{{ item }}</span>
                            </label>
                        </div>
                        <div class="p-6 pb-0" v-if="shopsFilters.vehiclesRepaired.length">
                            <h3 class="text-base mb-2">Vehicles Repaired</h3>
                            <label
                                v-for="(item, index) in shopsFilters.vehiclesRepaired" :for="'vehicle' + item | stripSpaces"
                                :key="index">
                                <input type="checkbox"
                                    :id="'vehicle' + item | stripSpaces"
                                    :value="item"
                                    class="hidden"
                                    ref="filterInputs"
                                    v-model="shopsFiltersEnabled.vehiclesRepaired">
                                <span class="btn-sm btn-white-bordered btn-filter-toggle border text-xs">{{ item }}</span>
                            </label>
                        </div>
                        <div class="p-6 pb-0" v-if="shopsFilters.languages.length">
                            <h3 class="text-base mb-2">Languages</h3>
                            <label
                                v-for="(item, index) in shopsFilters.languages" :for="'lang' + item"
                                :key="index">
                                <input type="checkbox"
                                    :id="'lang' + item | stripSpaces"
                                    :value="item"
                                    class="hidden"
                                    ref="filterInputs"
                                    v-model="shopsFiltersEnabled.languages">
                                <span class="btn-sm btn-white-bordered btn-filter-toggle border text-xs">{{ item }}</span>
                            </label>
                        </div>
                        <div class="p-6 pb-0" v-if="shopsFilters.customerServices.length">
                            <h3 class="text-base mb-2">Customer Services</h3>
                            <label
                                v-for="(item, index) in shopsFilters.customerServices" :for="'cusService' + item | stripSpaces"
                                :key="index">
                                <input type="checkbox"
                                    :id="'cusService' + item | stripSpaces"
                                    :value="item"
                                    class="hidden"
                                    ref="filterInputs"
                                    v-model="shopsFiltersEnabled.customerServices">
                                <span class="btn-sm btn-white-bordered btn-filter-toggle border text-xs">{{ item }}</span>
                            </label>
                        </div>
                        <div class="p-6 pb-0" v-if="shopsFilters.certificationTypes.length">
                            <h3 class="text-base mb-2">Certifications</h3>
                            <label
                                v-for="(item, index) in shopsFilters.certificationTypes" :for="'cert' + item | stripSpaces"
                                :key="index">
                                <input type="checkbox"
                                    :id="'cert' + item | stripSpaces"
                                    :value="item"
                                    class="hidden"
                                    ref="filterInputs"
                                    v-model="shopsFiltersEnabled.certificationTypes">
                                <span class="btn-sm btn-white-bordered btn-filter-toggle border text-xs">{{ item }}</span>
                            </label>
                        </div>
                    </div>
                </transition>
                <transition name="slide-right-full">
                    <div class="absolute pin-y w-full z-10 scrolling-touch overflow-y-auto"
                        v-if="!shopFiltersOpen && !shopDetailsOpen && shopsListFiltered !== null">
                        <transition name="fade-in">
                            <div v-if="!currentlySearching && shopsListFiltered.length > 0">
                                <a href="#" v-for="shop in shopsListFiltered"
                                    class="shop-list__item cursor-pointer hover:bg-grey-lighter relative
                                        text-black no-underline block focus:bg-grey-lighter"
                                    :class="[ shopDetailsLoadingId === shop.shopInfo.id ? 'loading' : '']"
                                    :key="shop.shopInfo.id"
                                    @click.prevent="openShopDetails($event, shop.shopInfo.id)">
                                    <div class="pl-6 pr-3 pt-3 pb-3 border-grey-light border-b">
                                        <div v-if="shop.shopInfo.shopLogo" class="hidden sm:block float-right">
                                            <img :src="shop.shopInfo.shopLogo.source"
                                                :alt="`${shop.shopInfo.name} Logo`"
                                                class="max-w-32 max-h-16 float-right">
                                        </div>
                                        <div class="text-lg text-green underline capitalize">{{ shop.shopInfo.name }}</div>
                                        <div class="hidden sm:block md:flex">
                                            <div class="flex-grow">
                                                <p>{{ shop.shopInfo.address.street }}<br>
                                                {{ shop.shopInfo.address.city }},
                                                {{ shop.shopInfo.address.state }},
                                                {{ shop.shopInfo.address.zipCode }}</p>
                                                <p v-if="shop.shopInfo.contactInfo.phone">{{ shop.shopInfo.contactInfo.phone }}</p>
                                            </div>
                                        </div>
                                        <CarcLogo v-if="shop.shopInfo.certifiedAutoRepair"
                                            class="w-16 mt-2 float-right"
                                            role="img"
                                            alt="Certified Auto Repair Logo"></CarcLogo>
                                        <div class="mt-2">
                                            <IconMapPin viewBox="0 0 24 24" class="inline-block w-4 h-4 mr-2"/>
                                            <strong>{{ shop.distanceFromDesiredLocation }} mi</strong>
                                            <span v-if="shop.shopInfo.shopHours">
                                                - <strong>{{ shopListStatus(shop.shopInfo.shopHours, shop.shopInfo.timezone) }}</strong>
                                                {{ shopListHours(shop.shopInfo.shopHours, shop.shopInfo.timezone) }}</span>
                                            <span v-else> - Call store for hours</span>
                                        </div>
                                    </div>
                                </a>
                            </div>
                            <div v-else-if="!currentlySearching && shopsListFiltered.length === 0" class="px-6 pt-12 text-center text-grey-darker">
                                <p class="text-xl">There are no shops in this area.</p>
                                <p>Try moving the map or searching a new location.</p>
                            </div>
                        </transition>
                    </div>
                </transition>
                <transition name="slide-left-full">
                    <div class="absolute pin-y w-full z-10 scrolling-touch overflow-y-auto"
                        v-if="shopDetailsOpen">
                        <div class="h-64 relative" v-if="'shopPhotos' in shopDetails">
                            <div class="h-64 siema">
                                <div class="w-full" v-for="(photo, index) in shopDetails.shopPhotos" :key="index">
                                    <img :src="photo.source" :alt="`${shopDetails.name} Photo ${index + 1}`" class="w-full">
                                </div>
                            </div>
                            <button class="siema__prev slider-btn pin-l" v-if="shopDetails.shopPhotos.length > 1">
                                <IconChevronLeft viewBox="0 0 24 24" class="inline-block w-10 h-10 mr"/>
                            </button>
                            <button class="siema__next slider-btn pin-r" v-if="shopDetails.shopPhotos.length > 1">
                                <IconChevronRight viewBox="0 0 24 24" class="inline-block w-10 h-10 mr"/>
                            </button>
                        </div>
                        <div class="p-6">
                            <h2 class="text-xl capitalize">{{ shopDetails.name }}</h2>
                            <div class="mt-4 flex">
                                <div class="w-2/3 flex-grow pr-3">
                                    <p>{{ shopDetails.address.street }}<br>
                                    {{ shopDetails.address.city }}, {{ shopDetails.address.state }}, {{ shopDetails.address.zipCode }}</p>
                                    <p class="mt-2">
                                        <span v-if="shopDetails.contactInfo.phone"><strong>Phone:</strong> {{ shopDetails.contactInfo.phone }}</span>
                                        <br>
                                        <span v-if="shopDetails.contactInfo.fax"><strong>Fax:</strong> {{ shopDetails.contactInfo.fax }}</span>
                                    </p>
                                    <ul class="mt-2 p-0 list-reset" v-if="Object.keys(shopDetails.shopUrls).length !== 0">
                                        <li v-if="shopDetails.shopUrls.homepage">
                                            <a :href="shopDetails.shopUrls.homepage"
                                                target="_blank"
                                                rel="noopener"
                                                class="text-red no-underline">
                                                <IconGlobe viewBox="0 0 24 24" class="inline-block w-4 h-4 mr-1"/>
                                                Website
                                            </a>
                                        </li>
                                        <li v-if="shopDetails.shopUrls.facebook">
                                            <a :href="shopDetails.shopUrls.facebook"
                                                target="_blank"
                                                rel="noopener"
                                                class="text-red no-underline">
                                                <IconFacebook viewBox="0 0 24 24" class="inline-block w-4 h-4 mr-1"/>
                                                Facebook
                                            </a>
                                        </li>
                                        <li v-if="shopDetails.shopUrls.twitter">
                                            <a :href="shopDetails.shopUrls.twitter"
                                                target="_blank"
                                                rel="noopener"
                                                class="text-red no-underline">
                                                <IconTwitter viewBox="0 0 24 24" class="inline-block w-4 h-4 mr-1"/>
                                                Twitter
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                                <div class="w-1/3" v-if="shopDetails.shopLogo">
                                    <img :src="shopDetails.shopLogo.source" :alt="`${shopDetails.name} Logo`" class="w-full">
                                </div>
                            </div>
                            <div class="mt-4">
                                <h3 class="text-base">Hours</h3>
                                <div v-if="shopDetails.shopHours">
                                    <table class="text-left">
                                        <tr v-for="day in shopDetails.shopHours" :key="day.day" :class="{'font-bold' : isShopDay(day.day)}">
                                            <td class="lowercase w-1/2"><span class="capitalize">{{ day.day }}</span></td>
                                            <td class="pl-4">
                                                <span v-if="day.openTime && day.closeTime">
                                                    {{ day.openTime | formatTime }} &mdash; {{ day.closeTime | formatTime }}
                                                </span>
                                                <span v-if="day.closed">Closed</span>
                                            </td>
                                        </tr>
                                    </table>
                                    <div class="mt-2 text-sm text-grey-darker" v-if="shopDetails.holidays">
                                        <strong>Closed Holidays</strong>
                                        <p>
                                            <span v-for="(holiday, index) in shopDetails.holidays" :key="holiday">
                                                {{ holiday }}<span v-if="index <= shopDetails.holidays.length">, </span>
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <div v-else>
                                    Call store for hours
                                </div>
                            </div>
                            <div class="mt-4" v-if="shopDetails.technicians && Object && Object.keys(shopDetails.technicians).length > 0">
                                <h3 class="text-base">Technicians</h3>
                                <p v-if="shopDetails.technicians.totalTechnicians">
                                    {{ shopDetails.technicians.totalTechnicians }}
                                    {{ (shopDetails.technicians.totalTechnicians === 1 ? ' technician with ' : ' technicians with a combined ') }}
                                    {{ shopDetails.technicians.totalYearsExperience || 'numerous' }} years of experience.
                                </p>
                                <table class="w-full text-left mt-4" v-if="shopDetails.technicians.technicianList">
                                    <tr>
                                        <th class="w-1/3">Type</th>
                                        <th class="w-1/3">Amount</th>
                                        <th>Exp.</th>
                                    </tr>
                                    <tr v-for="(tech, index) in shopDetails.technicians.technicianList" :key="index">
                                        <td class="w-1/3">{{ tech.description }}</td>
                                        <td class="w-1/3">{{ tech.count }}</td>
                                        <td v-if="tech.yearsExperience">{{ tech.yearsExperience }} yrs</td>
                                    </tr>
                                </table>
                            </div>
                            <div class="mt-4" v-if="shopDetails.languages">
                                <h3 class="text-base">Languages</h3>
                                <span v-for="(lang, index) in shopDetails.languages" :key="index">
                                    {{ lang }}<span v-if="index <= shopDetails.languages.length">, </span>
                                </span>
                            </div>
                            <div class="mt-4" v-if="shopDetails.vehiclesRepaired">
                                <h3 class="text-base">Vehicles We Repair</h3>
                                <ul class="list-reset">
                                    <li v-for="(vehicle, index) in shopDetails.vehiclesRepaired" :key="index">
                                        {{ vehicle }}
                                    </li>
                                </ul>
                            </div>
                            <div class="mt-4" v-if="shopDetails.repairServices">
                                <h3 class="text-base">Repair Services</h3>
                                <ul class="list-reset">
                                    <li v-for="(service, index) in shopDetails.repairServices" :key="index">
                                        {{ service }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
        <TheShopsMap :shopsListFiltered="shopsListFiltered" />
    </div>
</template>

<script>
import moment from 'moment-timezone';
import TheShopsSearch from '@/js/components/TheShopsSearch.vue';
import TheShopsMap from '@/js/components/TheShopsMap.vue';
import formatTime from '@/js/util/format-time';
import CarcLogo from '@/assets/svg/carc-logo.svg';
import IconMapPin from 'icon/map-pin.svg';
import IconChevronLeft from 'icon/chevron-left.svg';
import IconChevronRight from 'icon/chevron-right.svg';
import IconTrash2 from 'icon/trash-2.svg';
import IconSliders from 'icon/sliders.svg';
import IconGlobe from 'icon/globe.svg';
import IconFacebook from 'icon/facebook.svg';
import IconTwitter from 'icon/twitter.svg';
import Siema from 'siema';
import Vue from 'vue';
import { mapState, mapActions } from 'vuex';

export default {
    components: {
        TheShopsSearch,
        TheShopsMap,
        CarcLogo,
        IconMapPin,
        IconChevronLeft,
        IconChevronRight,
        IconSliders,
        IconTrash2,
        IconGlobe,
        IconFacebook,
        IconTwitter
    },
    data() {
        return {
            shopsCount: 0,
            shopsListFiltered: [],
            shopDetailsOpen: false,
            shopDetailsLoadingId: null,
            shopFiltersOpen: false,
            shopsFilters: {
                shopHours: [],
                technicians: [],
                repairServices: [],
                vehiclesRepaired: [],
                languages: [],
                customerServices: [],
                certificationTypes: []
            },
            countFiltersEnabled: 0,
            shopsFiltersEnabled: {
                shopHours: [],
                technicians: [],
                repairServices: [],
                vehiclesRepaired: [],
                languages: [],
                customerServices: [],
                certificationTypes: []
            },
            hasShopFilters: false
        };
    },
    mounted() {
        const { geolocation } = navigator;
        if (geolocation) {
            this.setGeoLocationLoading(true);
            const watchPosition = geolocation.watchPosition(
                (position) => {
                    const location = {
                        address: null,
                        city: null,
                        state: null,
                        zip: null,
                        country: null,
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                    };
                    this.selectGeoLocation(location);
                    geolocation.clearWatch(watchPosition);
                },
                (error) => {
                    switch (error.code) {
                    case error.PERMISSION_DENIED:
                        this.getShopsList();
                        break;
                    default:
                        // eslint-disable-next-line
                        console.error(error.message);
                    }
                }
            );
        } else {
            this.getShopsList();
        }
    },
    methods: {
        ...mapActions('shops', [
            'getShopsList',
            'getShopDetails',
            'setActiveShop'
        ]),
        ...mapActions('locations', [
            'selectGeoLocation',
            'setGeoLocationLoading'
        ]),
        openShopDetails(event, shopId) {
            this.shopDetailsLoadingId = shopId;
            if (this.activeShopId !== shopId) {
                this.setActiveShop(shopId);
            }
        },
        toggleShopFilters() {
            this.shopFiltersOpen = true;
            this.shopDetailsOpen = false;
        },
        toggleShopDetails() {
            this.shopDetailsLoadingId = null;
            this.shopFiltersOpen = false;
            this.shopDetailsOpen = true;
        },
        toggleShopList() {
            this.shopDetailsLoadingId = null;
            this.shopFiltersOpen = false;
            this.shopDetailsOpen = false;
            this.setActiveShop(null);
        },
        getShopFilters() {
            this.shopsFilters.technicians = this.concatFiltersArrays(this.shopsListFiltered, 'technicians');
            this.shopsFilters.repairServices = this.concatFiltersArrays(this.shopsListFiltered, 'repairServices');
            this.shopsFilters.vehiclesRepaired = this.concatFiltersArrays(this.shopsListFiltered, 'vehiclesRepaired');
            this.shopsFilters.languages = this.concatFiltersArrays(this.shopsListFiltered, 'languages');
            this.shopsFilters.customerServices = this.concatFiltersArrays(this.shopsListFiltered, 'customerServices');
            this.shopsFilters.certificationTypes = this.concatFiltersArrays(this.shopsListFiltered, 'certificationTypes');
        },
        concatFiltersArrays(array, key) {
            let newArray = [];
            array.forEach((item) => {
                if (key in item.shopInfo) {
                    if (key === 'certificationTypes') {
                        if (item.shopInfo.certifiedAutoRepair && !newArray.includes('Certified Auto Repair')) {
                            newArray = newArray.concat('Certified Auto Repair');
                        }
                    }
                    newArray = newArray.concat(item.shopInfo[key]);
                }
            });
            newArray = [...new Set(newArray)];
            return newArray.sort();
        },
        enabledFiltersEmpty() {
            const filtersEnabled = this.shopsFiltersEnabled;
            return filtersEnabled.constructor === Object && Object.keys(filtersEnabled).length === 0;
        },
        clearShopsFilters() {
            const enabledFiltersTypes = Object.keys(this.shopsFiltersEnabled);
            enabledFiltersTypes.forEach((item) => {
                this.shopsFiltersEnabled[item] = [];
            });
        },
        setCountFiltersEnabled() {
            const enabledFiltersTypes = Object.keys(this.shopsFiltersEnabled);
            let enabledFiltersCount = 0;
            enabledFiltersTypes.forEach((item) => {
                enabledFiltersCount += this.shopsFiltersEnabled[item].length;
            });
            this.countFiltersEnabled = enabledFiltersCount;
        },
        isShopDay(day) {
            const nowDay = moment().tz(this.timezone).isoWeekday();
            const openDay = this.getDayNumber(day);
            return nowDay === openDay;
        },
        shopListStatus(days) {
            const now = moment().tz(this.timezone);
            const today = days.find(day => day.day === moment(now).format('dddd'));
            return today.closed ? 'Closed' : 'Open';
        },
        shopListHours(days) {
            const now = moment().tz(this.timezone);
            const week = days;
            const today = week[0];
            const tomorrow = week[1];
            let storeHours = '24 Hours';
            if (today['24Hours']) {
                if (tomorrow.closed) {
                    storeHours = '(closes at Midnight)';
                } else {
                    storeHours = 'Open 24 hours';
                }
            } else if (today.closed) {
                const nextOpenDay = week.find(day => !day.closed && (day.openTime || day.byAppointment));
                if (nextOpenDay !== undefined) {
                    if (nextOpenDay.byAppointment) {
                        storeHours = `Call store on ${nextOpenDay.day} for appointment`;
                    } else if (today.index + 1 === (week.length + 1 || nextOpenDay.index)) {
                        const opensWhen = nextOpenDay['24Hours'] ? 'Midnight' : `${formatTime(nextOpenDay.openTime)}`;
                        storeHours = `(opens at ${opensWhen})`;
                    } else {
                        const opensWhen = nextOpenDay['24Hours'] ? 'Midnight' : `${formatTime(nextOpenDay.openTime)}`;
                        storeHours = `(opens ${nextOpenDay.day} at ${opensWhen})`;
                    }
                } else {
                    storeHours = 'Closed every day.';
                }
            } else if (today.byAppointment) {
                storeHours = 'Call store for appointment';
            } else {
                const openDate = `${moment(now).format('YYYY-MM-DD')}T${today.openTime}`;
                const closeDate = `${moment(now).format('YYYY-MM-DD')}T${today.closeTime}`;
                if (now.isBetween(openDate, closeDate)) {
                    storeHours = `(closes at ${formatTime(today.closeTime)})`;
                } else {
                    storeHours = `(opens at ${formatTime(today.openTime)})`;
                }
            }
            return storeHours;
        },
        getDayNumber(day) {
            switch (day) {
            case 'Monday':
                return 1;
            case 'Tuesday':
                return 2;
            case 'Wednesday':
                return 3;
            case 'Thursday':
                return 4;
            case 'Friday':
                return 5;
            case 'Saturday':
                return 6;
            case 'Sunday':
                return 7;
            default:
                return false;
            }
        }
    },
    computed: {
        ...mapState('locations', [
            'selectedLocation',
            'timezone',
            'geoLocationLoading'
        ]),
        ...mapState('shops', [
            'shopsList',
            'shopDetails',
            'activeShopId',
            'currentlySearching'
        ])
    },
    watch: {
        shopsList() {
            if (this.shopsList !== null) {
                this.shopsListFiltered = this.shopsList;
                this.shopsCount = this.shopsList.length;
                this.shopDetailsOpen = false;
            }
        },
        shopDetails() {
            if (this.shopDetails !== null) {
                this.toggleShopDetails();
                Vue.nextTick(() => {
                    if ('shopPhotos' in this.shopDetails) {
                        const siema = new Siema({
                            selector: '.siema',
                            duration: 200,
                            easing: 'ease-out',
                            perPage: 1,
                            startIndex: 0,
                            draggable: true,
                            multipleDrag: true,
                            threshold: 20,
                            loop: true,
                            rtl: false
                        });
                        if (this.shopDetails.shopPhotos.length > 1) {
                            const prev = document.querySelector('.siema__prev');
                            const next = document.querySelector('.siema__next');
                            prev.addEventListener('click', () => siema.prev());
                            next.addEventListener('click', () => siema.next());
                        }
                    }
                });
            }
        },
        activeShopId() {
            if (this.activeShopId !== null) {
                this.getShopDetails(this.activeShopId);
            }
        },
        selectedLocation() {
            this.getShopsList();
        },
        shopsListFiltered() {
            this.getShopFilters();
        },
        shopsFiltersEnabled: {
            handler() {
                if (this.shopsList !== null) {
                    this.setCountFiltersEnabled();
                    // Ready for Check-Ception
                    // Build a new shop array of items that pass all the checks
                    // Then check every filter category against the shop
                    this.shopsListFiltered = this.shopsList.filter(item => Object.keys(this.shopsFiltersEnabled).every((filter) => {
                        // Make sure the shop has every filter category that includes items
                        if (this.shopsFiltersEnabled[filter].length || this.shopsFiltersEnabled[filter] === 'Certified Auto Repair') {
                            // Make sure every item in a filter category is part of the shops corresponding category
                            return this.shopsFiltersEnabled[filter].every((filterItem) => {
                                if (filterItem === 'Certified Auto Repair') {
                                    return item.shopInfo.certifiedAutoRepair;
                                }
                                if (item.shopInfo[filter]) {
                                    return item.shopInfo[filter].includes(filterItem);
                                }
                                return false;
                            });
                        }
                        return true;
                    }));
                }
            },
            deep: true
        },
        currentlySearching() {
            if (this.currentlySearching) {
                this.clearShopsFilters();
            }
        }
    },
    filters: {
        formatTime,
        stripSpaces(string) {
            return string.replace(' ', '');
        }
    }
};
</script>

<style scoped>
.shop-list__item:after {
    content: '';
    position: absolute;
    background-color: black;
    height: 3px;
    bottom: 0;
    left: 0;
    transition: all 150ms ease-in-out;
    width: 0;
}
.shop-list__item.loading:after {
    width: 100%;
}
.slide-left-full-enter-active,
.slide-left-full-leave-active,
.slide-right-full-enter-active,
.slide-right-full-leave-active {
    transition: all .3s ease;
    width: 100%;
    position: absolute;
}
.slide-left-full-enter,
.slide-left-full-leave-to {
    transform: translateX(100%);
    opacity: 0;
    position: absolute;
}
.slide-right-full-enter,
.slide-right-full-leave-to {
    transform: translateX(-100%);
    opacity: 0;
    position: absolute;
}
.fade-in-enter-active,
.fade-in-leave-active {
    transition: all .3s ease;
}
.fade-in-enter,
.fade-in-leave-to {
    opacity: 0;
}


.slide-left-20-enter-active,
.slide-left-20-leave-active,
.slide-right-20-enter-active,
.slide-right-20-leave-active {
    transition: all .3s ease;
    width: 100%;;
}
.slide-left-20-enter,
.slide-left-20-leave-to {
    transform: translateX(20%);
    opacity: 0;
    position: absolute;
}
.slide-right-20-enter,
.slide-right-20-leave-to {
    transform: translateX(-20%);
    opacity: 0;
    position: absolute;
}
</style>
